import {
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  message,
} from "antd";
import WeightageInput from "./WeightageInput";
import { RightOutlined, DeleteOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import COLORS from "common/Colors";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import TextLink from "./TextLink";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { getUserInfo, getUserList } from "redux/selectors";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const { TextArea } = Input;

const Wrap = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
  }
  .perspective-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    padding: 10px;
    display: flex;
    justify-content: end;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin: 20px -20px 0;
  }
  .review-checkbox-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .review-checkbox-label {
      width: 140px;
      flex: 0 0 140px;
    }

    .ant-picker {
      width: 180px;
    }
  }
  .ant-form-item {
    margin-bottom: 0;
  }
  .revision-reason-card {
    background: #fafafa;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;

    .revision-reason-title {
      color: #8c8c8c;
      margin-bottom: 4px;
    }
  }
  .ant-table-thead > tr > th {
    background: #C0CDE2 !important;
    font-weight: 600;
    font-size: 13px;
    color: #000000;
  }
  .ant-table-sticky-holder {
    background: #fff;
    z-index: 3;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 8px 12px;
  }
  .planning-group-row > td {
    background: #fafafa;
  }
  .planning-spacer-row {
    pointer-events: none;
  }
  .planning-spacer-row > td {
    height: 10px;
    padding: 0 !important;
    border: none !important;
    background: #fff;
  }
`;

const PERSPECTIVES = [
  "Financial Perspective",
  "Customer Perspective",
  "Internal Business processes",
  "Learning & Growth Perspective",
];

const PERIOD_OPTIONS = [
  { label: "Yearly Appraisal", value: "Yearly" },
  { label: "Half Yearly Appraisal", value: "Half Yearly" },
  { label: "Quarterly Appraisal", value: "Quarterly" },
  { label: "Probationary Appraisal", value: "Probationary" },
];

const QUARTER_OPTIONS = [
  { label: "Q1", value: "Q1" },
  { label: "Q2", value: "Q2" },
  { label: "Q3", value: "Q3" },
  { label: "Q4", value: "Q4" },
];

// A Quarterly appraisal only ever covers Q1; Half Yearly (and the shorter
// Probationary appraisal) only ever covers Q1 & Q2 - offering the full Q1-Q4
// list regardless of period let users pick a quarter their appraisal period
// couldn't possibly reach.
const quarterOptionsForPeriod = (currentPeriod) => {
  if (currentPeriod === "Quarterly") return QUARTER_OPTIONS.slice(0, 1);
  if (currentPeriod === "Half Yearly" || currentPeriod === "Probationary") {
    return QUARTER_OPTIONS.slice(0, 2);
  }
  return QUARTER_OPTIONS;
};

const MAX_ROWS = 5;
const MAX_TRAINING_ROWS = 10;

const FIXED_SOFT_SKILLS = [
  { objectives: "Integrity, Honesty (Trusted)", weightage: 18 },
  { objectives: "Effective communication, Inspiration (Hunter)", weightage: 18 },
  { objectives: "Responsibility, buck stops here (Leader)", weightage: 18 },
  { objectives: "Reliability, Resilience (Gatherer)", weightage: 18 },
  { objectives: "Teamwork, Mentorship (Builder)", weightage: 18 },
  { objectives: "Wow Factor (Your Differentiator)", weightage: 10 },
];

const emptyRow = (perspective, sno) => ({
  perspective,
  sno,
  measurementdimension: "",
  objectives: "",
  weightage: 0,
  comments: "",
});

const initialRows = () => {
  const grouped = {};
  PERSPECTIVES.forEach((p) => (grouped[p] = [emptyRow(p, 1)]));
  return grouped;
};

const buildGroupedFromPerspectives = (perspectives = []) => {
  const grouped = {};
  PERSPECTIVES.forEach((p) => (grouped[p] = []));
  perspectives.forEach((row) => {
    if (!PERSPECTIVES.includes(row.perspective)) return;
    if (grouped[row.perspective].length >= MAX_ROWS) return;
    grouped[row.perspective].push({
      perspective: row.perspective,
      sno: grouped[row.perspective].length + 1,
      measurementdimension: row.measurementdimension || "",
      objectives: row.objectives || "",
      weightage: row.weightage || 0,
      comments: row.comments || "",
    });
  });
  PERSPECTIVES.forEach((p) => {
    if (grouped[p].length === 0) grouped[p] = [emptyRow(p, 1)];
  });
  return grouped;
};

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

const emptyRolloverRow = (sno) => ({ sno, objectives: "", weightage: 0, comments: "" });

const emptyTrainingRow = (sno) => ({ sno, skill: "", method: "", quarter: null });

const REVIEW_LABEL_TO_KEY = { Q1: "q1", "Mid Year": "midyear", Q3: "q3" };
const REVIEW_KEY_TO_LABEL = { q1: "Q1", midyear: "Mid Year", q3: "Q3" };

const FillKpiContract = () => {
  const history = useHistory();
  const location = useLocation();
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const contractid = location.state?.contractid;
  const isResubmit = Boolean(contractid);
  // Reviewing KPI > "Edit & Approve": the appraiser opens the appraisee's
  // self-KPI submission in this same form, corrects it and approves it in one
  // step instead of sending it back with "Request Revision".
  const isEditApprove = isResubmit && Boolean(location.state?.editApprove);
  const [form] = Form.useForm();

  const goToList = () =>
    history.push("/performance", {
      id: "kpiplanning",
      subTab: isEditApprove ? "myteam" : "mine",
    });
  const onCancel = () =>
    isEditApprove
      ? history.push("/performance-kpi-review-kpi", { contractid })
      : goToList();

  const me = (userList || []).find((u) => u.userid === userInfo?.userid);

  const [period, setPeriod] = useState();
  const [reviews, setReviews] = useState({
    q1: { checked: false, date: null },
    midyear: { checked: false, date: null },
    q3: { checked: false, date: null },
  });
  const [rowsByPerspective, setRowsByPerspective] = useState(initialRows());
  const [rolloverRows, setRolloverRows] = useState([emptyRolloverRow(1)]);
  const [trainingRows, setTrainingRows] = useState([emptyTrainingRow(1)]);
  const [softSkillsEditable, setSoftSkillsEditable] = useState(false);
  const [softSkillsRows, setSoftSkillsRows] = useState(FIXED_SOFT_SKILLS);
  const [declineReason, setDeclineReason] = useState("");
  const [contract, setContract] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previousAppraisals, setPreviousAppraisals] = useState([]);
  const [previousModalOpen, setPreviousModalOpen] = useState(false);
  const [selectedPreviousId, setSelectedPreviousId] = useState(null);
  const [previousLoading, setPreviousLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyTargetPerspective, setCopyTargetPerspective] = useState(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  useEffect(() => {
    // The sticky footer is clamped to the padding edge of this scroll
    // container, so the layout's own bottom padding always shows as a gap
    // below it - drop it to 0 while this page is mounted and restore after.
    const main = document.querySelector(".ant-layout-content");
    if (!main) return;
    const previousPaddingBottom = main.style.paddingBottom;
    main.style.paddingBottom = "0";
    return () => {
      main.style.paddingBottom = previousPaddingBottom;
    };
  }, []);

  useEffect(() => {
    if (!isResubmit) return;

    const fetchContract = async () => {
      setLoading(true);
      try {
        const { data, code } = await CALL_API(
          `performance-kpi-management/contract/${contractid}`,
          "get"
        );
        if (code === STATUS_CODE.SUCCESS && data) {
          setContract(data);
          form.setFieldsValue({
            fullname: data.fullname,
            period: data.period,
            startdate: data.startdate ? dayjs(data.startdate) : null,
          });
          setPeriod(data.period);

          const nextReviews = {
            q1: { checked: false, date: null },
            midyear: { checked: false, date: null },
            q3: { checked: false, date: null },
          };
          (data.progressreviewdates || []).forEach((item) => {
            const key = REVIEW_LABEL_TO_KEY[item.label];
            if (key) {
              nextReviews[key] = { checked: true, date: item.date ? dayjs(item.date) : null };
            }
          });
          setReviews(nextReviews);

          const grouped = initialRows();
          (data.perspectives || []).forEach((row) => {
            if (!PERSPECTIVES.includes(row.perspective)) return;
            if (grouped[row.perspective].length === 1 && !grouped[row.perspective][0].objectives) {
              grouped[row.perspective] = [];
            }
            grouped[row.perspective].push({ ...row, sno: grouped[row.perspective].length + 1 });
          });
          PERSPECTIVES.forEach((p) => {
            if (grouped[p].length === 0) grouped[p] = [emptyRow(p, 1)];
          });
          setRowsByPerspective(grouped);

          if (data.rolloverresponsibilities?.length) {
            setRolloverRows(data.rolloverresponsibilities);
          }
          if (data.softskillsassessment?.length) {
            setSoftSkillsRows(data.softskillsassessment);
          }
          if (data.trainingdevelopmentplan?.length) {
            setTrainingRows(data.trainingdevelopmentplan);
          }
          setDeclineReason(data.declinereason || "");
          form.setFieldsValue({ appraiseecomment: data.appraiseecomment });
        }
      } catch (error) {
        console.error("Error fetching contract:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractid]);

  const onValuesChange = (changed) => {
    if (changed.period) {
      setPeriod(changed.period);
      // Q1 stays valid for every period - only clear a checkpoint that
      // actually contradicts the newly-chosen period, not the whole set.
      if (["Quarterly", "Half Yearly", "Probationary"].includes(changed.period)) {
        setReviews((prev) => ({
          ...prev,
          midyear: { checked: false, date: null },
          q3: { checked: false, date: null },
        }));
      }
    }
  };

  const toggleReview = (key, checked) => {
    setReviews((prev) => ({ ...prev, [key]: { ...prev[key], checked } }));
  };

  const setReviewDate = (key, date) => {
    setReviews((prev) => ({ ...prev, [key]: { ...prev[key], date } }));
  };

  const updateRow = (perspective, index, field, value) => {
    setRowsByPerspective((prev) => {
      const rows = [...prev[perspective]];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, [perspective]: rows };
    });
  };

  const addRow = (perspective) => {
    setRowsByPerspective((prev) => {
      const rows = prev[perspective];
      if (rows.length >= MAX_ROWS) return prev;
      return {
        ...prev,
        [perspective]: [...rows, emptyRow(perspective, rows.length + 1)],
      };
    });
  };

  const removeRow = (perspective, index) => {
    setRowsByPerspective((prev) => {
      const rows = prev[perspective]
        .filter((_, i) => i !== index)
        .map((row, i) => ({ ...row, sno: i + 1 }));
      return { ...prev, [perspective]: rows.length ? rows : [emptyRow(perspective, 1)] };
    });
  };

  const weightageTotal = (perspective) =>
    rowsByPerspective[perspective].reduce((sum, row) => sum + (Number(row.weightage) || 0), 0);

  const mainWeightageTotal = PERSPECTIVES.reduce(
    (sum, perspective) => sum + weightageTotal(perspective),
    0
  );

  const nameFor = (userid) => (userList || []).find((u) => u.userid === userid)?.fullname || "-";

  const onClickCopyPrevious = async (perspective = null) => {
    // Previous plans belong to the appraisee, not whoever has the form open.
    const appraiseeUserId = isEditApprove ? contract?.appraiseeuserid : userInfo?.userid;
    if (!appraiseeUserId) {
      message.info("No previous KPI plan found for this employee");
      return;
    }
    setCopyTargetPerspective(perspective);
    setPreviousLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/previousappraisals/${appraiseeUserId}`,
        "get"
      );
      const list = code === STATUS_CODE.SUCCESS ? data || [] : [];
      if (!list.length) {
        message.info("No previous KPI plan found for this employee");
        return;
      }
      setPreviousAppraisals(list);
      setSelectedPreviousId(list[0].id);
      setPreviousModalOpen(true);
    } catch (error) {
      console.error("Error fetching previous appraisals:", error);
      message.error("Failed to fetch previous KPI planning");
    } finally {
      setPreviousLoading(false);
    }
  };

  const onCopyObjectives = async () => {
    const target = previousAppraisals.find((item) => item.id === selectedPreviousId);
    if (!target) return;
    setCopyLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/contract/${target.contractid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS && data) {
        const grouped = buildGroupedFromPerspectives(data.perspectives);
        if (copyTargetPerspective) {
          setRowsByPerspective((prev) => ({
            ...prev,
            [copyTargetPerspective]: grouped[copyTargetPerspective],
          }));
          message.success(`${copyTargetPerspective} objectives copied from previous KPI Planning`);
        } else {
          setRowsByPerspective(grouped);
          message.success("Objectives copied from previous KPI Planning");
        }
        setPreviousModalOpen(false);
      } else {
        message.error("Failed to copy objectives");
      }
    } catch (error) {
      console.error("Error copying previous objectives:", error);
      message.error("Failed to copy objectives");
    } finally {
      setCopyLoading(false);
    }
  };

  const previousAppraisalColumns = [
    { title: "S.No", key: "sno", width: 60, render: (_, __, index) => index + 1 },
    { title: "Period", dataIndex: "period", key: "period" },
    { title: "Created on", key: "createdon", render: (_, row) => formatDate(row.createdon) },
    { title: "Attended on", key: "attendedon", render: (_, row) => formatDate(row.attendedon) },
    {
      title: "Closed by",
      key: "closedby",
      render: (_, row) => `${nameFor(row.appraiseruserid)}; ${formatDate(row.closedon)}`,
    },
  ];

  const updateRolloverRow = (index, field, value) => {
    setRolloverRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], [field]: value };
      return rows;
    });
  };

  const addRolloverRow = () => {
    setRolloverRows((prev) =>
      prev.length >= MAX_ROWS ? prev : [...prev, emptyRolloverRow(prev.length + 1)]
    );
  };

  const removeRolloverRow = (index) => {
    setRolloverRows((prev) => {
      const rows = prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, sno: i + 1 }));
      return rows.length ? rows : [emptyRolloverRow(1)];
    });
  };

  const updateTrainingRow = (index, field, value) => {
    setTrainingRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], [field]: value };
      return rows;
    });
  };

  const addTrainingRow = () => {
    setTrainingRows((prev) =>
      prev.length >= MAX_TRAINING_ROWS ? prev : [...prev, emptyTrainingRow(prev.length + 1)]
    );
  };

  const removeTrainingRow = (index) => {
    setTrainingRows((prev) => {
      const rows = prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, sno: i + 1 }));
      return rows.length ? rows : [emptyTrainingRow(1)];
    });
  };

  const updateSoftSkillWeightage = (index, value) => {
    setSoftSkillsRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], weightage: value };
      return rows;
    });
  };

  const softSkillsTotal = softSkillsRows.reduce((sum, row) => sum + (Number(row.weightage) || 0), 0);

  const rolloverFilledRows = rolloverRows.filter((row) => row.objectives);
  const rolloverTotal = rolloverRows.reduce((sum, row) => sum + (Number(row.weightage) || 0), 0);

  const validatePlanning = () => {
    const errors = [];
    PERSPECTIVES.forEach((perspective) => {
      const rows = rowsByPerspective[perspective];
      const filledRows = rows.filter((row) => row.measurementdimension && row.objectives && row.comments);
      if (filledRows.length === 0) {
        errors.push(
          `${perspective}: add at least one row with Measurement Dimension, Objective and Planning Assumptions / Comments`
        );
      }
    });
    if (mainWeightageTotal !== 100) {
      errors.push(`Planning: Weightage must be exactly 100 (currently ${mainWeightageTotal}%)`);
    }
    if (rolloverFilledRows.length > 0 && rolloverTotal !== 100) {
      errors.push(
        `Rollover Responsibility from Previous Appraisal: weightage must total 100% (currently ${rolloverTotal}%)`
      );
    }
    if (softSkillsTotal !== 100) {
      errors.push(`Individual (Soft Skills) Assessment: weightage must total 100% (currently ${softSkillsTotal}%)`);
    }
    // Same "started" rule as Rollover - any field filled in makes the whole
    // row mandatory, not just once "skill" specifically has content.
    trainingRows.forEach((row) => {
      if (!(row.skill || row.method || row.quarter)) return;
      if (!row.skill || !row.method || !row.quarter) {
        errors.push(
          `Training & Development Plan: row ${row.sno} needs Skill, Method of development and Planned / Expected quarter filled in`
        );
      }
    });
    Object.entries(REVIEW_KEY_TO_LABEL).forEach(([key, label]) => {
      if (reviews[key].checked && !reviews[key].date) {
        errors.push(`${label} Review: start date is required`);
      }
    });
    return errors;
  };

  const textAreaProps = { autoSize: { minRows: 1, maxRows: 4 } };

  // The 4 perspectives render as ONE continuous table (one shared sticky
  // header) - perspective titles and "Add another" links are injected as
  // full-width rows within the same table via colSpan, rather than each
  // perspective having its own separate Table/header.
  const buildPlanningRows = () => {
    const rows = [];
    PERSPECTIVES.forEach((perspective, i) => {
      if (i > 0) rows.push({ key: `spacer-${perspective}`, isSpacer: true });
      rows.push({ key: `group-${perspective}`, isGroupHeader: true, perspective });
      rowsByPerspective[perspective].forEach((row, index) => {
        rows.push({ ...row, key: `${perspective}-${row.sno}`, localIndex: index });
      });
      if (rowsByPerspective[perspective].length < MAX_ROWS) {
        rows.push({ key: `add-${perspective}`, isAddRow: true, perspective });
      }
    });
    return rows;
  };

  const fullSpanCell = (record) =>
    record.isGroupHeader || record.isAddRow || record.isSpacer ? { colSpan: 6 } : {};
  const hiddenCell = (record) =>
    record.isGroupHeader || record.isAddRow || record.isSpacer ? { colSpan: 0 } : {};

  const planningColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      onCell: fullSpanCell,
      render: (_, record) => {
        if (record.isSpacer) {
          return null;
        }
        if (record.isGroupHeader) {
          return <div className="perspective-title m-0">{record.perspective}</div>;
        }
        if (record.isAddRow) {
          const isLastPerspective = record.perspective === PERSPECTIVES[PERSPECTIVES.length - 1];
          return (
            <div
              className={`d-flex align-items-center ${
                isLastPerspective ? "justify-content-between" : "justify-content-end"
              }`}
              style={{ gap: 12 }}
            >
              {isLastPerspective && (
                <span style={{ color: mainWeightageTotal === 100 ? COLORS.SUCCESS : COLORS.ERROR }}>
                  Weightage: {mainWeightageTotal}%
                </span>
              )}
              <div className="d-flex align-items-center" style={{ gap: 12 }}>
                <CopyOutlined
                  title="Copy from Previous KPI Planning"
                  style={{ cursor: "pointer", color: COLORS.PRIMARY }}
                  onClick={() => onClickCopyPrevious(record.perspective)}
                />
                <Divider type="vertical" style={{ margin: 0 }} />
                <TextLink onClick={() => addRow(record.perspective)}>
                  <PlusOutlined /> Add another (Max {MAX_ROWS} rows)
                </TextLink>
              </div>
            </div>
          );
        }
        return record.sno;
      },
    },
    {
      title: "Measurement Dimensions",
      key: "measurementdimension",
      onCell: hiddenCell,
      render: (_, record) => (
        <TextArea
          {...textAreaProps}
          status={attemptedSubmit && !record.measurementdimension ? "error" : ""}
          value={record.measurementdimension}
          onChange={(e) =>
            updateRow(record.perspective, record.localIndex, "measurementdimension", e.target.value)
          }
        />
      ),
    },
    {
      title: "Objectives",
      key: "objectives",
      onCell: hiddenCell,
      render: (_, record) => (
        <TextArea
          {...textAreaProps}
          status={attemptedSubmit && !record.objectives ? "error" : ""}
          value={record.objectives}
          onChange={(e) => updateRow(record.perspective, record.localIndex, "objectives", e.target.value)}
        />
      ),
    },
    {
      title: "Weightage (%)",
      key: "weightage",
      width: 130,
      onCell: hiddenCell,
      render: (_, record) => (
        <WeightageInput
          value={record.weightage}
          onChange={(val) => updateRow(record.perspective, record.localIndex, "weightage", val)}
        />
      ),
    },
    {
      title: "Appraisee's Comment *",
      key: "comments",
      onCell: hiddenCell,
      render: (_, record) => (
        <TextArea
          {...textAreaProps}
          status={attemptedSubmit && !record.comments ? "error" : ""}
          value={record.comments}
          onChange={(e) => updateRow(record.perspective, record.localIndex, "comments", e.target.value)}
        />
      ),
    },
    {
      key: "actions",
      width: 50,
      onCell: hiddenCell,
      render: (_, record) => (
        <DeleteOutlined
          style={{ cursor: "pointer" }}
          onClick={() => removeRow(record.perspective, record.localIndex)}
        />
      ),
    },
  ];

  const rolloverColumns = [
    { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
    {
      title: "Objectives",
      key: "objectives",
      render: (_, row, index) => (
        <TextArea
          {...textAreaProps}
          value={row.objectives}
          onChange={(e) => updateRolloverRow(index, "objectives", e.target.value)}
        />
      ),
    },
    {
      title: "Weightage (%)",
      key: "weightage",
      width: 130,
      render: (_, row, index) => (
        <WeightageInput
          value={row.weightage}
          onChange={(val) => updateRolloverRow(index, "weightage", val)}
        />
      ),
    },
    {
      title: "Appraisee's Comment",
      key: "comments",
      render: (_, row, index) => (
        <TextArea
          {...textAreaProps}
          value={row.comments}
          onChange={(e) => updateRolloverRow(index, "comments", e.target.value)}
        />
      ),
    },
    {
      key: "actions",
      width: 50,
      render: (_, row, index) => (
        <DeleteOutlined style={{ cursor: "pointer" }} onClick={() => removeRolloverRow(index)} />
      ),
    },
  ];

  const softSkillsColumns = [
    { title: "Objectives", dataIndex: "objectives", key: "objectives" },
    {
      title: "Weightage (%)",
      key: "weightage",
      width: 130,
      render: (_, row, index) =>
        softSkillsEditable ? (
          <WeightageInput
            value={row.weightage}
            onChange={(val) => updateSoftSkillWeightage(index, val)}
          />
        ) : (
          row.weightage
        ),
    },
  ];

  const trainingColumns = [
    { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
    {
      title: "Skill to be developed / strengthened",
      key: "skill",
      render: (_, row, index) => (
        <TextArea
          {...textAreaProps}
          value={row.skill}
          onChange={(e) => updateTrainingRow(index, "skill", e.target.value)}
        />
      ),
    },
    {
      title: "Method of development, i.e. whether on-the-job , formal training or other.",
      key: "method",
      render: (_, row, index) => (
        <TextArea
          {...textAreaProps}
          value={row.method}
          onChange={(e) => updateTrainingRow(index, "method", e.target.value)}
        />
      ),
    },
    {
      title: "Planned / Expected quarter",
      key: "quarter",
      width: 160,
      render: (_, row, index) => (
        <Select
          options={quarterOptionsForPeriod(period)}
          value={row.quarter}
          onChange={(val) => updateTrainingRow(index, "quarter", val)}
          style={{ width: "100%" }}
          placeholder="Select"
        />
      ),
    },
    {
      key: "actions",
      width: 50,
      render: (_, row, index) => (
        <DeleteOutlined style={{ cursor: "pointer" }} onClick={() => removeTrainingRow(index)} />
      ),
    },
  ];

  const submitKpi = async (values) => {
    const progressreviewdates = [];
    if (reviews.q1.checked) progressreviewdates.push({ label: "Q1", date: reviews.q1.date });
    if (reviews.midyear.checked)
      progressreviewdates.push({ label: "Mid Year", date: reviews.midyear.date });
    if (reviews.q3.checked) progressreviewdates.push({ label: "Q3", date: reviews.q3.date });

    const perspectives = Object.values(rowsByPerspective)
      .flat()
      .filter((row) => row.measurementdimension || row.objectives);

    const payload = {
      fullname: isResubmit ? contract?.fullname : values.fullname,
      period: isResubmit ? contract?.period : values.period,
      startdate: values.startdate,
      progressreviewdates,
      perspectives,
      rolloverresponsibilities: rolloverRows.filter((row) => row.objectives),
      softskillsassessment: softSkillsRows,
      trainingdevelopmentplan: trainingRows.filter((row) => row.skill),
      appraiseecomment: values.appraiseecomment || "",
    };

    setSubmitLoading(true);
    try {
      if (isEditApprove) {
        const { code, data, message: apiMessage } = await CALL_API(
          `performance-kpi-management/editapprovekpi/${contractid}`,
          "patch",
          payload
        );
        // The handler answers unknown actions with SUCCESS + "Invalid
        // resource" and no data, so require the updated contract back.
        if (code === STATUS_CODE.SUCCESS && data?.id) {
          message.success("KPI edited and approved successfully");
          goToList();
        } else {
          message.error(
            apiMessage && apiMessage !== "Invalid resource" ? apiMessage : "Failed to approve KPI"
          );
        }
        return;
      }

      const { code } = isResubmit
        ? await CALL_API(`performance-kpi-management/submitkpi/${contractid}`, "patch", payload)
        : await CALL_API("performance-kpi-management/submitkpi", "post", {
            ...payload,
            appraiseeuserid: userInfo?.userid,
            appraiseruserid: me?.reportingmanager,
            employeeid: me?.employeeid,
          });
      if (code === STATUS_CODE.SUCCESS) {
        message.success("KPI submitted for review successfully");
        goToList();
      } else {
        message.error("Failed to submit KPI");
      }
    } catch (error) {
      console.error("Error submitting KPI:", error);
      message.error(isEditApprove ? "Failed to approve KPI" : "Failed to submit KPI");
    } finally {
      setSubmitLoading(false);
    }
  };

  const onSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return; // antd renders the inline field errors
    }

    setAttemptedSubmit(true);
    const errors = validatePlanning();
    if (errors.length) {
      // Mandatory Planning fields are called out with a red outline instead
      // (see planningColumns), so no toast needed for those. Weightage
      // totals, rollover, soft skills and training rules aren't visible as
      // inline field state anywhere, so they still need a message.
      const nonFieldErrors = errors.filter(
        (error) =>
          !PERSPECTIVES.some((p) => error.startsWith(`${p}:`))
      );
      nonFieldErrors.forEach((error) => message.error(error));
      return;
    }

    Modal.confirm({
      title: isEditApprove
        ? "Remember, this can't be changed. Save your changes and approve this KPI?"
        : "Are you sure you want to send your KPI to the appraiser for review?",
      icon: null,
      centered: true,
      okText: isEditApprove ? "Yes, Approve" : "Yes, Send",
      cancelText: "No, Cancel",
      okButtonProps: { type: "primary" },
      onOk: () => submitKpi(values),
    });
  };

  return (
    <Wrap>
      <div className="breadcrumb mb-3">
        <p
          className="lite-text p-0 m-0"
          style={{ cursor: "pointer" }}
          onClick={goToList}
        >
          Performance Management
        </p>
        &nbsp;
        <RightOutlined className="mt-1" />
        &nbsp;
        <p className="m-0 p-0">
          {isEditApprove ? "Edit & Approve KPI" : isResubmit ? "Revising KPI" : "Filling KPI"}
        </p>
      </div>

      {isResubmit ? (
        <h6>
          {contract?.period || "Yearly"} Appraisal –{" "}
          <span className="lite-text">Key responsibilities / targets for the appraisal</span>
        </h6>
      ) : (
        <h6>Filling KPIs</h6>
      )}

      <Spin spinning={loading}>
        {isEditApprove && (
          <div className="revision-reason-card">
            <div className="revision-reason-title">
              Editing {contract?.fullname || "the appraisee"}'s KPI submission
            </div>
            <div>
              Your changes are saved and the KPI is approved in one step. It won't go back to the
              appraisee for another review.
            </div>
          </div>
        )}

        {isResubmit && !isEditApprove && declineReason && (
          <div className="revision-reason-card">
            <div className="revision-reason-title">Reason for requesting revision</div>
            <div>{declineReason}</div>
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          initialValues={{ fullname: me?.fullname }}
          onValuesChange={onValuesChange}
        >
          <Row gutter={[16, 16]} className="my-3">
            {!isResubmit ? (
              <>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="fullname"
                    label="Name of the employee"
                    rules={[{ required: true, message: "Name of the employee is required" }]}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="period"
                    label="Period"
                    rules={[{ required: true, message: "Period is required" }]}
                  >
                    <Select options={PERIOD_OPTIONS} style={{ width: "100%" }} placeholder="Select" />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <Col xs={24} md={12}>
                <div className="lite-text">KPI defined by</div>
                <div className="text-bold">
                  {contract?.kpidefinedby === "Self"
                    ? `Appraisee : ${contract?.fullname || "-"}`
                    : `Appraiser : ${contract?.kpidefinedby || "-"}`}
                </div>
              </Col>
            )}
            <Col xs={24} md={12}>
              <Form.Item
                name="startdate"
                label="Start date to attend appraisal"
                rules={[{ required: true, message: "Start date to attend appraisal is required" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            {period && period !== "Quarterly" && (
              <Col xs={24} md={12}>
                <div className="lite-text mb-2">
                  If you need progress review, select the sessions you want and set a start date for each
                </div>
                <div className="review-checkbox-row">
                  <Checkbox
                    className="review-checkbox-label"
                    checked={reviews.q1.checked}
                    onChange={(e) => toggleReview("q1", e.target.checked)}
                  >
                    Q1 Review
                  </Checkbox>
                  <DatePicker
                    disabled={!reviews.q1.checked}
                    value={reviews.q1.date}
                    onChange={(d) => setReviewDate("q1", d)}
                    format="DD-MM-YYYY"
                  />
                </div>
                {period === "Yearly" && (
                  <>
                    <div className="review-checkbox-row">
                      <Checkbox
                        className="review-checkbox-label"
                        checked={reviews.midyear.checked}
                        onChange={(e) => toggleReview("midyear", e.target.checked)}
                      >
                        Mid-Year Review
                      </Checkbox>
                      <DatePicker
                        disabled={!reviews.midyear.checked}
                        value={reviews.midyear.date}
                        onChange={(d) => setReviewDate("midyear", d)}
                        format="DD-MM-YYYY"
                      />
                    </div>
                    <div className="review-checkbox-row">
                      <Checkbox
                        className="review-checkbox-label"
                        checked={reviews.q3.checked}
                        onChange={(e) => toggleReview("q3", e.target.checked)}
                      >
                        Q3 Review
                      </Checkbox>
                      <DatePicker
                        disabled={!reviews.q3.checked}
                        value={reviews.q3.date}
                        onChange={(d) => setReviewDate("q3", d)}
                        format="DD-MM-YYYY"
                      />
                    </div>
                  </>
                )}
              </Col>
            )}
          </Row>

        <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 56 }}>
          <div>
            <h6 className="m-0">Planning *</h6>
            <p className="lite-text m-0">
              Describe specific Objectives or other developmental activities to be
              undertaken by Appraisee during the appraisal period.
            </p>
          </div>
          <TextLink onClick={() => onClickCopyPrevious()} loading={previousLoading}>
            <CopyOutlined /> Copy from Previous KPI Planning
          </TextLink>
        </div>

        <Table
          columns={planningColumns}
          dataSource={buildPlanningRows()}
          pagination={false}
          rowKey={(row) => row.key}
          sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
          style={{ marginTop: 32 }}
          rowClassName={(record) =>
            record.isGroupHeader ? "planning-group-row" : record.isSpacer ? "planning-spacer-row" : ""
          }
        />

        {rowsByPerspective[PERSPECTIVES[PERSPECTIVES.length - 1]].length >= MAX_ROWS && (
          // Weightage normally rides along on the last perspective's "Add
          // another" row (same row, not a new one) - only needed as its own
          // line here once that perspective is full and has no add-row left
          // to attach to.
          <div className="mb-2">
            <span style={{ color: mainWeightageTotal === 100 ? COLORS.SUCCESS : COLORS.ERROR }}>
              Weightage: {mainWeightageTotal}%
            </span>
          </div>
        )}

        <div className="perspective-title" style={{ marginTop: 32 }}>
          Rollover Responsibility from Previous Appraisal
        </div>
        <Table
          columns={rolloverColumns}
          dataSource={rolloverRows}
          pagination={false}
          rowKey={(row) => `rollover-${row.sno}`}
          footer={() => (
            <div className="d-flex justify-content-between align-items-center">
              {rolloverFilledRows.length > 0 ? (
                <span style={{ color: rolloverTotal === 100 ? COLORS.SUCCESS : COLORS.ERROR }}>
                  Weightage: {rolloverTotal}%
                </span>
              ) : (
                <span />
              )}
              {rolloverRows.length < MAX_ROWS ? (
                <TextLink onClick={addRolloverRow}>
                  <PlusOutlined /> Add another (Max {MAX_ROWS} rows)
                </TextLink>
              ) : (
                <span />
              )}
            </div>
          )}
        />

        <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 56 }}>
          <div className="perspective-title m-0">Individual (Soft Skills) Assessment</div>
          {softSkillsEditable ? (
            <TextLink onClick={() => setSoftSkillsRows(FIXED_SOFT_SKILLS)}>
              Use Default Weightage
            </TextLink>
          ) : (
            <TextLink onClick={() => setSoftSkillsEditable(true)}>Edit Weightage</TextLink>
          )}
        </div>
        <Table
          columns={softSkillsColumns}
          dataSource={softSkillsRows}
          pagination={false}
          rowKey={(row) => row.objectives}
          footer={
            softSkillsEditable
              ? () => (
                  <div className="text-end">
                    <span style={{ color: softSkillsTotal === 100 ? COLORS.SUCCESS : COLORS.ERROR }}>
                      Total Weightage: {softSkillsTotal}%
                    </span>
                  </div>
                )
              : undefined
          }
        />

        <div className="perspective-title mt-4">Training & Development Plan</div>
        <p className="lite-text m-0">
          Based on the strengths and limitations identified in the previous review, indicate the
          work experience and/or training suggested during the plan period.
        </p>
        <Table
          columns={trainingColumns}
          dataSource={trainingRows}
          pagination={false}
          rowKey={(row) => `training-${row.sno}`}
          footer={() =>
            trainingRows.length < MAX_TRAINING_ROWS ? (
              <TextLink onClick={addTrainingRow}>
                <PlusOutlined /> Add another (Max {MAX_TRAINING_ROWS} rows)
              </TextLink>
            ) : null
          }
        />

          <Form.Item name="appraiseecomment" label="Appraisee's Comment" className="mt-4">
            <TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>
        </Form>

        <div className="footer">
          <ButtonOutlined onClick={onCancel}>Cancel</ButtonOutlined>
          <ButtonFilled type="primary" disabled={submitLoading} onClick={onSubmit}>
            {isEditApprove ? "Save & Approve" : "Send for Review"}
          </ButtonFilled>
        </div>
      </Spin>

      <Modal
        title="Previous Appraisals"
        open={previousModalOpen}
        onCancel={() => setPreviousModalOpen(false)}
        width={800}
        centered
        footer={[
          <ButtonOutlined key="cancel" onClick={() => setPreviousModalOpen(false)}>
            Cancel
          </ButtonOutlined>,
          <ButtonFilled
            key="copy"
            type="primary"
            loading={copyLoading}
            disabled={!selectedPreviousId}
            onClick={onCopyObjectives}
          >
            Copy Objectives
          </ButtonFilled>,
        ]}
      >
        <Table
          columns={previousAppraisalColumns}
          dataSource={previousAppraisals}
          pagination={false}
          rowKey={(row) => row.id}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedPreviousId ? [selectedPreviousId] : [],
            onChange: (keys) => setSelectedPreviousId(keys[0]),
          }}
          onRow={(row) => ({
            style: { cursor: "pointer" },
            onClick: () => setSelectedPreviousId(row.id),
          })}
        />
      </Modal>
    </Wrap>
  );
};

export default FillKpiContract;
